import React, { useEffect, useMemo, useState } from 'react';
import NoPermissionNew from '@intelligence/components/NoPermissionNew';
import { isNotEmpty } from '@intelligence/v3/utils/common';
import { fetchIntelligenceUserInfo, fetchPermissionRules } from './service';

import type { IntelligencePagePermissionRule, IntelligenceUserInfo } from './service';

interface PermissionGuardProps {
  children: React.ReactNode;
  pageId: number | string;
  style?: React.CSSProperties;
}

/**
 * 统一用户组名称后再比较，容忍配置中的大小写、首尾空格和全角 ASCII 符号差异。
 * 例如 ` Test（Studio） ` 与 `test(studio)` 会被视为同一个用户组。
 */
const normalizeGroupName = (value: string) =>
  value
    .replace(/\u3000/g, ' ')
    .replace(/[\uFF01-\uFF5E]/g, (character) =>
      String.fromCharCode(character.charCodeAt(0) - 0xfee0),
    )
    .trim()
    .toLowerCase();

/** 判断用户组配置是否命中当前用户所属组织的 group_name。 */
const matchesGroupPermission = (values: string[] | undefined, candidates: string[]) => {
  const normalizedCandidates = new Set(candidates.map(normalizeGroupName));
  return values?.some((value) => normalizedCandidates.has(normalizeGroupName(value))) || false;
};

/**
 * 配置值包含 @ 时只按完整邮箱匹配，否则只按企微 ID 匹配。
 * 两类身份不交叉比较，避免邮箱前缀与企微 ID 相同时误命中。
 */
const matchesUserPermission = (values: string[] | undefined, userInfo: IntelligenceUserInfo) =>
  values?.some((value) =>
    value.includes('@') ? value === userInfo.email : value === userInfo.wecomId,
  ) || false;

const PermissionGuard: React.FC<PermissionGuardProps> = ({ children, pageId, style }) => {
  const [permissionRules, setPermissionRules] = useState<IntelligencePagePermissionRule[] | null>(null);
  const [userInfo, setUserInfo] = useState<IntelligenceUserInfo | null>(null);
  const [loadedPageId, setLoadedPageId] = useState<string | null>(null);
  const [requestFailed, setRequestFailed] = useState(false);

  useEffect(() => {
    let canceled = false;

    const requestPageId = String(pageId);
    setRequestFailed(false);

    Promise.all([fetchPermissionRules(), fetchIntelligenceUserInfo()])
      .then(([rules, intelligenceUserInfo]) => {
        if (!canceled) {
          setPermissionRules(rules);
          setUserInfo(intelligenceUserInfo);
          setLoadedPageId(requestPageId);
        }
      })
      .catch(() => {
        if (!canceled) {
          setPermissionRules(null);
          setUserInfo(null);
          setLoadedPageId(requestPageId);
          setRequestFailed(true);
        }
      });

    return () => {
      canceled = true;
    };
  }, [pageId]);

  const isLoading = loadedPageId !== String(pageId);

  const hasPermission = useMemo(() => {
    if (!permissionRules || !userInfo) return false;

    const normalizedPageId = String(pageId);
    // pages 兼容数字和字符串 pageId；当前页面未配置规则时默认放行。
    const matchedRules = permissionRules.filter((rule) =>
      rule.pages?.some((configuredPageId) => String(configuredPageId) === normalizedPageId),
    );

    if (!isNotEmpty(matchedRules)) return true;

    const { groups } = userInfo;
    const userExclude = matchedRules.flatMap((rule) => rule.userExclude || []);
    const userInclude = matchedRules.flatMap((rule) => rule.userInclude || []);
    const groupExclude = matchedRules.flatMap((rule) => rule.groupExclude || []);
    const groupInclude = matchedRules.flatMap((rule) => rule.groupInclude || []);

    // 优先级：用户黑名单 > 用户白名单 > 组黑名单 > 组白名单 > 默认放行。
    if (matchesUserPermission(userExclude, userInfo)) return false;
    if (matchesUserPermission(userInclude, userInfo)) return true;
    if (matchesGroupPermission(groupExclude, groups)) return false;
    if (matchesGroupPermission(groupInclude, groups)) return true;

    return true;
  }, [pageId, permissionRules, userInfo]);

  if (isLoading || !hasPermission) {
    return (
      <div style={style}>
        <NoPermissionNew loading={isLoading} showTips={!requestFailed} />
      </div>
    );
  }

  return <>{children}</>;
};

export default PermissionGuard;
